function _extends() { return _extends = Object.assign ? Object.assign.bind() : function (n) { for (var e = 1; e < arguments.length; e++) { var t = arguments[e]; for (var r in t) ({}).hasOwnProperty.call(t, r) && (n[r] = t[r]); } return n; }, _extends.apply(null, arguments); }
import React, { useContext, useRef, useEffect, useState, memo } from 'react';
import classNames from 'classnames';
import PropTypes from 'prop-types';
import { Button } from '../../Button';
import { Heading } from '../../Heading';
import { Tag } from '../../Tag';
import { Text } from '../../Text';
import { AccordionContext, ACCORDION_TYPE } from '../Accordion';
import styles from '@dtsl/css-accordion/dist/index.module.css';
import ChevronDown from '@dtsl/icons/dist/icons/react/ChevronDown';
import ChevronUp from '@dtsl/icons/dist/icons/react/ChevronUp';
function AccordionPanel(props) {
  const tagRef = useRef(null);
  const panelRef = useRef(null);
  const {
    panelContent = {
      icon: null,
      title: '',
      titleProps: {},
      helpText: null,
      helpTextProps: {}
    },
    onClick,
    itemIdx,
    className = ''
  } = props;
  const {
    tagsData = []
  } = panelContent || {};
  const {
    visibleAccordionItems,
    setVisibleAccordionItems,
    accordionType
  } = useContext(AccordionContext);
  const [stackTagBlock, setStackTagBlock] = useState(false);
  const isPanelContentNode = /*#__PURE__*/React.isValidElement(panelContent);
  const handleAllOpenable = clickedItemIdx => {
    setVisibleAccordionItems(prevState => {
      const newVisibilityOfAccordions = [...prevState];
      newVisibilityOfAccordions[clickedItemIdx] = !newVisibilityOfAccordions[clickedItemIdx];
      return newVisibilityOfAccordions;
    });
  };
  const handleSingleOpenable = clickedItemIdx => {
    setVisibleAccordionItems(prevState => {
      const newVisibilityOfAccordions = prevState.map((isVisible, index) => index === clickedItemIdx ? !isVisible : false);
      return newVisibilityOfAccordions;
    });
  };
  const handleAccordionClick = e => {
    e.preventDefault();
    accordionType === ACCORDION_TYPE.MULTI_OPENABLE ? handleAllOpenable(itemIdx) : handleSingleOpenable(itemIdx);
    onClick(e, itemIdx);
  };
  const panelContentClasses = classNames({
    [styles['sib-accordion__panel-content']]: tagsData?.length,
    [styles['sib-accordion__panel-content--column']]: stackTagBlock,
    [className]: !!className
  });
  const tagsContainerClasses = classNames({
    [styles['sib-accordion__panel-tagsContainer']]: true
  });
  useEffect(() => {
    const element = tagRef?.current;
    if (!element) return;
    const resizeObserver = new ResizeObserver(() => {
      // When tag width is <= 1/3 of panel then it will display on the right side of header container.
      //When tag width is more than 1/3 of panel it should wrap below the header container.
      if (tagRef.current) {
        if (tagRef.current?.clientWidth > 0.33 * panelRef?.current?.clientWidth) {
          setStackTagBlock(true);
        } else {
          setStackTagBlock(false);
        }
      }
    });
    resizeObserver.observe(element); //When the tag title updated dynamically
    resizeObserver.observe(panelRef?.current);
    return () => {
      // Cleanup the observer by unobserving all elements
      resizeObserver.disconnect();
    };
  }, []);
  const standardPanelBody = /*#__PURE__*/React.createElement(React.Fragment, null, /*#__PURE__*/React.createElement("div", {
    ref: panelRef,
    className: panelContentClasses
  }, /*#__PURE__*/React.createElement("div", null, /*#__PURE__*/React.createElement("div", {
    className: styles['sib-accordion__panel-head-container']
  }, panelContent?.icon ? /*#__PURE__*/React.createElement("div", {
    className: styles['sib-accordion__icon-container']
  }, panelContent.icon()) : null, /*#__PURE__*/React.createElement(Heading, _extends({
    size: "sm",
    level: 2
  }, panelContent?.titleProps), panelContent?.title || '')), /*#__PURE__*/React.createElement(Text, _extends({
    size: "sm",
    fontFamily: "regular",
    tag: "p"
  }, panelContent?.helpTextProps, {
    style: {
      color: 'var(--sib-color_content-secondary)',
      paddingTop: '8px',
      ...(panelContent?.icon && {
        marginLeft: '1.5rem'
      }),
      ...panelContent?.helpTextProps?.style
    }
  }), panelContent?.helpText)), tagsData?.length > 0 ? /*#__PURE__*/React.createElement("div", {
    ref: tagRef,
    className: tagsContainerClasses
  }, tagsData?.length > 0 && tagsData?.slice(0, 3)?.map(item => {
    const {
      className: tagClassName
    } = item;
    const tagsClassNames = classNames({
      [styles['sib-accordion__tag-element']]: true,
      [tagClassName]: tagClassName
    });
    return /*#__PURE__*/React.createElement(Tag, _extends({}, item, {
      className: tagsClassNames,
      closable: false,
      key: item.label
    }));
  })) : ''));
  const panelWrapperClasses = classNames({
    [styles['sib-accordion__panel']]: true,
    [styles['sib-accordion__panel--clickable']]: !isPanelContentNode
  });
  return isPanelContentNode ? /*#__PURE__*/React.createElement("div", {
    className: panelWrapperClasses
  }, /*#__PURE__*/React.createElement("div", {
    className: panelContentClasses,
    id: `accordionItem-${itemIdx}`
  }, panelContent), /*#__PURE__*/React.createElement(Button, {
    iconAlone: true,
    ariaLabel: "button",
    "aria-describedby": `accordionItem-${itemIdx}`,
    "aria-expanded": visibleAccordionItems[itemIdx] ? 'true' : 'false',
    icon: visibleAccordionItems[itemIdx] ? /*#__PURE__*/React.createElement(ChevronUp, null) : /*#__PURE__*/React.createElement(ChevronDown, null),
    variant: "tertiary",
    onClick: handleAccordionClick
  })) : /*#__PURE__*/React.createElement("button", {
    className: panelWrapperClasses,
    "aria-expanded": visibleAccordionItems[itemIdx] ? 'true' : 'false',
    onClick: handleAccordionClick
  }, standardPanelBody, /*#__PURE__*/React.createElement("span", {
    className: styles['sib-accordion__arrow']
  }, visibleAccordionItems[itemIdx] ? /*#__PURE__*/React.createElement(ChevronUp, null) : /*#__PURE__*/React.createElement(ChevronDown, null)));
}
AccordionPanel.propTypes = {
  panelContent: PropTypes.oneOfType([PropTypes.node, PropTypes.shape({
    icon: PropTypes.node,
    title: PropTypes.string.isRequired,
    titleProps: PropTypes.object,
    helpText: PropTypes.string,
    helpTextProps: PropTypes.object
  })]).isRequired,
  onClick: PropTypes.func,
  itemIdx: PropTypes.number,
  className: PropTypes.string
};
const AccordionPanelMemo = /*#__PURE__*/memo(AccordionPanel);
export default AccordionPanelMemo;